import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AdminNavigation } from "../components/navigation/AdminNavigation";
import { toast } from "sonner";
import {
  ArrowLeft,
  ShieldCheck,
  Star,
  ChevronRight,
  Clock,
  Globe,
  Music,
  Sparkles,
  Loader2,
} from "lucide-react";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /admin/idol-applications — full queue of Top 100 Global Music Idols 2027
 * submissions awaiting review. Admin can score each candidate (six-axis
 * weighted rubric) and advance them to the next round or eliminate them.
 */
const AdminIdolApplications = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [scoring, setScoring] = useState({
    talent: 0,
    originality: 0,
    stage_presence: 0,
    fan_engagement: 0,
    entrepreneurial_ability: 0,
    global_potential: 0,
  });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/idol-2027/applications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApps(data.applications || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load applications");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const submitScore = async (appId) => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/idol-2027/applications/${appId}/score`,
        scoring,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Scored — weighted total ${data.score}/100`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Score failed");
    } finally {
      setBusy(false);
    }
  };

  const decide = async (appId, status, nextRound) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/idol-2027/applications/${appId}/advance`,
        { status, round: nextRound || undefined },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(status === "advanced" ? "Advanced to next round" : status === "top100" ? "Marked TOP 100 🎉" : "Eliminated");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-black text-white pb-24" data-testid="admin-idol-applications-page">
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur border-b border-[#1A2030]">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-lg border border-[#1A2030] hover:bg-[#1A2030] flex items-center justify-center"
            data-testid="idol-apps-back-btn"
            aria-label="Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Link to="/home" className="flex items-center gap-2">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
            <span className="hidden sm:inline text-white font-bold">Admin · Idol Applications</span>
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <Star className="w-5 h-5 text-[#F0C464]" />
            <span className="text-sm text-[#8A92A0]" data-testid="idol-apps-count">{apps.length} pending</span>
          </div>
        </div>
      </header>
      <AdminNavigation />

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-2">Top 100 Global Music Idols · 2027</h1>
          <p className="text-[#8A92A0] max-w-2xl">
            Applications submitted for the Top 100 competition. Score each candidate across six weighted axes (talent 25% · originality 20% · stage 15% · fans 15% · entrepreneurial 15% · global 10%) then advance or eliminate.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#EC008C]" /></div>
        ) : apps.length === 0 ? (
          <div className="card p-16 text-center" data-testid="idol-apps-empty">
            <Star className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">Inbox zero</h3>
            <p className="text-[#8A92A0] text-sm">No idol applications pending right now.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {apps.map((a) => (
              <button
                key={a.application_id}
                onClick={() => { setSelected(a); setScoring(a.score_breakdown || { talent:0, originality:0, stage_presence:0, fan_engagement:0, entrepreneurial_ability:0, global_potential:0 }); }}
                className="card p-5 text-left hover:border-[#F0C464]/40 transition-colors"
                data-testid={`idol-app-row-${a.application_id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-white font-bold text-lg">{a.stage_name || a.legal_name}</p>
                    <p className="text-xs text-[#8A92A0]">{a.legal_name}</p>
                  </div>
                  {a.score != null && (
                    <span className="px-2 py-1 rounded bg-[#F0C464]/20 text-[#F0C464] text-xs font-bold">
                      {a.score}/100
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-[#8A92A0]">
                  <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> {a.country || "—"} · {a.city || "—"}</span>
                  <span className="flex items-center gap-1"><Music className="w-3 h-3" /> {a.primary_talent || "—"}</span>
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest bg-[#0A0E18] border border-[#1A2030] rounded px-2 py-1 text-[#8A92A0]">
                    Round {a.current_round || 1}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest bg-[#0A0E18] border border-[#1A2030] rounded px-2 py-1 text-[#8A92A0]">
                    {a.status || "submitted"}
                  </span>
                  <span className="ml-auto flex items-center gap-1 text-xs text-[#4A5260]">
                    <Clock className="w-3 h-3" /> {a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : ""}
                    <ChevronRight className="w-4 h-4" />
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4" data-testid="idol-app-modal" onClick={() => setSelected(null)}>
          <div className="bg-[#0A0E18] border border-[#1A2030] rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs text-[#8A92A0] mb-1">{selected.application_id}</p>
                <h2 className="font-['Unbounded'] text-2xl font-bold text-white">{selected.stage_name || selected.legal_name}</h2>
                <p className="text-sm text-[#8A92A0]">{selected.email} · {selected.country}, {selected.city}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-[#8A92A0] hover:text-white text-xl leading-none">✕</button>
            </div>

            <div className="space-y-3 text-sm mb-6">
              <div><p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Biography</p><p className="text-white whitespace-pre-wrap">{selected.biography || "—"}</p></div>
              <div><p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Vision</p><p className="text-white whitespace-pre-wrap">{selected.vision || "—"}</p></div>
              <div><p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">World Impact</p><p className="text-white whitespace-pre-wrap">{selected.world_impact || "—"}</p></div>
            </div>

            <div className="border-t border-[#1A2030] pt-4">
              <h3 className="font-['Unbounded'] text-sm font-bold text-white mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#F0C464]" /> Six-Axis Score</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["talent","Talent","25%"],
                  ["originality","Originality","20%"],
                  ["stage_presence","Stage Presence","15%"],
                  ["fan_engagement","Fan Engagement","15%"],
                  ["entrepreneurial_ability","Entrepreneurial","15%"],
                  ["global_potential","Global Potential","10%"],
                ].map(([k, label, w]) => (
                  <label key={k} className="text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[#8A92A0]">{label} <span className="text-[#4A5260]">({w})</span></span>
                      <span className="text-white font-bold">{scoring[k]}</span>
                    </div>
                    <input
                      type="range" min="0" max="100"
                      value={scoring[k]}
                      onChange={(e) => setScoring({ ...scoring, [k]: parseInt(e.target.value, 10) })}
                      className="w-full accent-[#F0C464]"
                      data-testid={`score-slider-${k}`}
                    />
                  </label>
                ))}
              </div>
              <button
                onClick={() => submitScore(selected.application_id)}
                disabled={busy}
                className="mt-4 h-10 px-4 rounded-full bg-[#F0C464] text-black font-bold text-sm hover:bg-[#EBB84C] disabled:opacity-50 w-full"
                data-testid="submit-score-btn"
              >
                {busy ? "Saving…" : "Save score"}
              </button>
            </div>

            <div className="border-t border-[#1A2030] pt-4 mt-4 flex flex-wrap gap-2">
              <button onClick={() => decide(selected.application_id, "advanced", (selected.current_round || 1) + 1)} className="h-9 px-4 rounded-full bg-[#8DB87A] text-black font-bold text-xs" data-testid="advance-btn">Advance to R{(selected.current_round || 1) + 1}</button>
              <button onClick={() => decide(selected.application_id, "top100")} className="h-9 px-4 rounded-full bg-gradient-to-r from-[#F0C464] via-[#EC008C] to-[#FF3399] text-black font-bold text-xs" data-testid="top100-btn">🏆 Top 100</button>
              <button onClick={() => decide(selected.application_id, "eliminated")} className="h-9 px-4 rounded-full border border-[#4A5260] text-[#8A92A0] hover:bg-[#1A2030] font-bold text-xs" data-testid="eliminate-btn">Eliminate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminIdolApplications;
